/**
 * InlineEditField — text field for inline editing of match values.
 * Standard variant with explicit Save / Cancel buttons.
 * AC-3.3
 */
import React, { JSX, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';

interface InlineEditFieldProps {
  initialValue: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
  label?: string;
  disabled?: boolean;
  saving?: boolean;
}

const InlineEditField = ({
  initialValue,
  onCommit,
  onCancel,
  label,
  disabled = false,
  saving = false,
}: InlineEditFieldProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = (): void => {
    const value = inputRef.current?.value ?? '';
    onCommit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape' && !saving) {
      onCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <TextField
        inputRef={inputRef}
        defaultValue={initialValue}
        label={label}
        variant="standard"
        size="small"
        fullWidth
        disabled={disabled || saving}
        autoFocus
        onKeyDown={handleKeyDown}
        inputProps={{
          enterKeyHint: 'done',
          style: { fontSize: 14, fontWeight: 600 },
        }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={disabled || saving}
          onClick={handleSave}
          data-testid="edit-save-btn"
          startIcon={
            saving ? (
              <CircularProgress size={12} sx={{ color: 'inherit' }} />
            ) : undefined
          }
        >
          <span>{saving ? 'Saving' : 'Save'}</span>
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={disabled || saving}
          onClick={onCancel}
          data-testid="edit-cancel-btn"
        >
          <span>Cancel</span>
        </Button>
      </Box>
    </Box>
  );
};

export default InlineEditField;
